import React, { useState, useEffect, useCallback } from 'react';
import { revenueSourceAPI } from '../services/api';
import { DashboardLayout } from '../components/DashboardLayout';
import { useGym } from '../contexts/GymContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, DollarSign } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#22C55E', '#10B981', '#EF4444', '#8B5CF6', '#F59E0B', '#10B981'];

// Helper to get color from types array
const getTypeColor = (typeName, types, index) => {
  const typeObj = types.find(t => t.name === typeName);
  return typeObj?.color || COLORS[index % COLORS.length];
};

export const RevenueSourcesPage = () => {
  const { selectedGym } = useGym();
  const [sources, setSources] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [formData, setFormData] = useState({
    month: new Date().toLocaleString('pt-BR', { month: 'long' }),
    year: new Date().getFullYear(),
    source_type: '',
    amount: '',
    description: ''
  });

  const months = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];

  const loadData = useCallback(async () => {
    if (!selectedGym) return;
    try {
      const [sourcesRes, typesRes] = await Promise.all([
        revenueSourceAPI.getAll(selectedGym.id),
        revenueSourceAPI.getTypes(selectedGym.id)
      ]);
      setSources(sourcesRes.data);
      // Types now come as objects with id, name, color
      setTypes(typesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar origens de receita');
    } finally {
      setLoading(false);
    }
  }, [selectedGym]);

  useEffect(() => {
    if (selectedGym) {
      loadData();
    }
  }, [selectedGym, loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGym) return;
    
    try {
      const data = {
        gym_id: selectedGym.id,
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editingSource) {
        await revenueSourceAPI.update(editingSource.id, data);
        toast.success('Origem atualizada com sucesso!');
      } else {
        await revenueSourceAPI.create(data);
        toast.success('Origem adicionada com sucesso!');
      }

      loadData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Erro ao salvar origem de receita');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar esta origem de receita?')) {
      try {
        await revenueSourceAPI.delete(id);
        toast.success('Origem deletada com sucesso!');
        loadData();
      } catch (error) {
        toast.error('Erro ao deletar origem');
      }
    }
  };

  const handleEdit = (source) => {
    setEditingSource(source);
    setFormData({
      month: source.month,
      year: source.year,
      source_type: source.source_type,
      amount: source.amount.toString(),
      description: source.description || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingSource(null);
    setFormData({
      month: new Date().toLocaleString('pt-BR', { month: 'long' }),
      year: new Date().getFullYear(),
      source_type: '',
      amount: '',
      description: ''
    });
  };

  // Month order mapping for chronological sorting
  const monthOrder = {
    'janeiro': 1, 'fevereiro': 2, 'março': 3, 'abril': 4, 'maio': 5, 'junho': 6,
    'julho': 7, 'agosto': 8, 'setembro': 9, 'outubro': 10, 'novembro': 11, 'dezembro': 12
  };

  const totalByType = sources.reduce((acc, source) => {
    acc[source.source_type] = (acc[source.source_type] || 0) + source.amount;
    return acc;
  }, {});

  const chartData = Object.entries(totalByType).map(([name, value]) => ({ name, value }));
  const totalRevenue = sources.reduce((sum, source) => sum + source.amount, 0);

  // Sort sources chronologically (most recent first)
  const sortedSources = [...sources].sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year;
    return monthOrder[b.month.toLowerCase()] - monthOrder[a.month.toLowerCase()];
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-white uppercase tracking-tight">
              Origens de Receita
            </h1>
            <p className="text-zinc-400 mt-2">De onde vem o faturamento da sua empresa</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-source-button" className="btn-primary flex items-center gap-2">
                <Plus size={20} />
                Adicionar Origem
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading text-[#22C55E] uppercase">
                  {editingSource ? 'Editar Origem' : 'Nova Origem de Receita'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Mês</Label>
                    <select
                      data-testid="month-input"
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                      className="input-field w-full mt-1"
                      required
                    >
                      {months.map(month => (
                        <option key={month} value={month}>{month.charAt(0).toUpperCase() + month.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Ano</Label>
                    <Input
                      data-testid="year-input"
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      className="input-field mt-1"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300">Tipo de Origem</Label>
                  <select
                    data-testid="source-type-input"
                    value={formData.source_type}
                    onChange={(e) => setFormData({ ...formData, source_type: e.target.value })}
                    className="input-field w-full mt-1"
                    required
                  >
                    <option value="">Selecione um tipo</option>
                    {types.map(type => (
                      <option key={type.id || type.name} value={type.name}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-zinc-300">Valor (R$)</Label>
                  <Input
                    data-testid="amount-input"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="input-field mt-1"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Descrição (opcional)</Label>
                  <Input
                    data-testid="description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-field mt-1"
                    placeholder="Ex: Mensalidades de janeiro"
                  />
                </div>
                <Button data-testid="save-source-button" type="submit" className="w-full btn-primary">
                  {editingSource ? 'Atualizar' : 'Salvar'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Card className="glass-panel lg:col-span-5">
            <CardHeader>
              <CardTitle className="text-2xl font-heading text-white uppercase">Distribuição por Tipo</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length === 0 ? (
                <p className="text-zinc-500 text-center py-16">Nenhuma origem cadastrada</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(24, 24, 27, 0.95)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card className="glass-panel lg:col-span-7">
            <CardHeader>
              <CardTitle className="text-2xl font-heading text-white uppercase">Total por Tipo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(totalByType).map(([type, amount], index) => {
                  const typeColor = getTypeColor(type, types, index);
                  return (
                  <div key={type} className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/5">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: typeColor }}
                      />
                      <span className="text-white font-medium">{type}</span>
                    </div>
                    <span className="text-2xl font-bold text-[#22C55E] font-numbers">R$ {amount.toFixed(2)}</span>
                  </div>
                )})}
                <div className="flex items-center justify-between p-4 bg-[#22C55E]/10 rounded-lg border border-[#22C55E]/20 mt-4">
                  <span className="text-white font-bold uppercase">Total Geral</span>
                  <span className="text-3xl font-bold text-[#22C55E] font-numbers">R$ {totalRevenue.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card data-testid="sources-list" className="glass-panel">
          <CardHeader>
            <CardTitle className="text-2xl font-heading text-white uppercase">Histórico de Origens</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-zinc-500 py-8">Carregando...</p>
            ) : sources.length === 0 ? (
              <p className="text-center text-zinc-500 py-8">Nenhuma origem cadastrada</p>
            ) : (
              <div className="space-y-2">
                {sortedSources.map(source => {
                  const typeObj = types.find(t => t.name === source.source_type);
                  const typeColor = typeObj?.color || '#22C55E';
                  return (
                  <div
                    key={source.id}
                    className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/5 hover:border-[#22C55E]/30 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span 
                          className="px-3 py-1 rounded-full text-xs font-medium uppercase"
                          style={{
                            backgroundColor: `${typeColor}15`,
                            color: typeColor
                          }}
                        >
                          {source.source_type}
                        </span>
                        <p className="text-white font-medium">{source.description || source.source_type}</p>
                      </div>
                      <p className="text-sm text-zinc-500 mt-1">{source.month} / {source.year}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-2xl font-bold text-[#22C55E] font-numbers">R$ {source.amount.toFixed(2)}</p>
                      <div className="flex gap-2">
                        <button
                          data-testid={`edit-source-${source.id}`}
                          onClick={() => handleEdit(source)}
                          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-[#22C55E] transition-colors"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          data-testid={`delete-source-${source.id}`}
                          onClick={() => handleDelete(source.id)}
                          className="p-2 rounded-lg bg-white/5 hover:bg-red-500/10 text-zinc-400 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                )})}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};